import {
    ApolloClient,
    from,
    HttpLink,
    HttpOptions,
    InMemoryCache
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import fetch from 'isomorphic-unfetch';
import { IncomingHttpHeaders } from 'http';
import { logError, logInfo } from '@zoopla/logger';
import { getPropertyKey } from './typePolicies';

let apolloClientInstance: ApolloClient<unknown> | null = null;

let fetchOptions: HttpOptions['fetchOptions'];
if (typeof window === 'undefined') {
    // On the server, create an agentkeepalive client
    // so that we reuse socket connections to the GraphQL API endpoint
    // See: https://www.npmjs.com/package/agentkeepalive
    // And: https://www.lob.com/blog/use-http-keep-alive
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const HttpsAgent = require('agentkeepalive').HttpsAgent;
    fetchOptions = { agent: new HttpsAgent() };
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const PREFIX = 'GraphQL';

    let messages: string[] = [];

    if (graphQLErrors) {
        messages = messages.concat(
            graphQLErrors
                .filter(
                    ({ path, message }) =>
                        // dont log known listings validation errors
                        !(
                            operation.operationName === 'getListingData' &&
                            path &&
                            path[0].toString() === 'searchResults' &&
                            message.startsWith('422:')
                        ) &&
                        // dont log expired listing errors
                        !(
                            operation.operationName === 'getListingDetails' &&
                            path &&
                            path[0].toString() === 'listing' &&
                            message === 'Listing Expired'
                        ) &&
                        !(
                            operation.operationName === 'housePricesResults' &&
                            path &&
                            path[0].toString() === 'propertiesSearch' &&
                            message === 'UPRN is required'
                        )
                )
                .map(
                    ({ message, path }) =>
                        `[${PREFIX}] [graphQLError]: Operation: ${operation.operationName}, Message: ${message}, Path: ${path}`
                )
        );
    }

    if (networkError) {
        messages.push(
            `[${PREFIX}] [networkError]: Operation: ${operation.operationName}, Message: ${networkError}`
        );
    }

    messages.forEach((message) =>
        process.env.NODE_ENV === 'test' ? logInfo(message) : logError(message)
    );
});

const createApolloClient = (
    initialState = {},
    headers?: IncomingHttpHeaders
) => {
    const httpLink = new HttpLink({
        uri: process.env['NEXT_PUBLIC_GRAPHQL_API'],
        credentials: 'include',
        fetch,
        fetchOptions,
        headers: headers
            ? {
                  cookie: headers.cookie,
                  'X-Api-Key': process.env['NEXT_PUBLIC_API_KEY']
              }
            : { 'X-Api-Key': process.env['NEXT_PUBLIC_API_KEY'] }
    });

    return new ApolloClient({
        link: from([errorLink, httpLink]),
        ssrMode: typeof window === 'undefined',
        cache: new InMemoryCache({
            typePolicies: {
                Query: {
                    fields: {
                        user: {
                            merge: true
                        },
                        property: {
                            merge: true
                        }
                    }
                },
                User: {
                    keyFields: ['userId'],
                    merge: true
                },
                Property: {
                    keyFields: getPropertyKey
                },
                ListingData: {
                    merge: true
                }
            }
        }).restore(initialState)
    });
};

const getApolloClient = (initialState = {}, headers?: IncomingHttpHeaders) => {
    // Reuse client on the client-side if available
    if (!apolloClientInstance) {
        apolloClientInstance = createApolloClient(initialState, headers);
    }

    return apolloClientInstance;
};

export { createApolloClient, getApolloClient, apolloClientInstance };
